import { useUserPreferences } from '@solinkcorp/authentication';
import {
  LANGUAGES,
  type LocaleDefinition,
  type LocaleJsonFile,
  type LocaleName,
} from '@solinkcorp/locale';
import { enCALocale } from '@solinkcorp/locale/en-CA';
import { enGBLocale } from '@solinkcorp/locale/en-GB';
import { enUSLocale } from '@solinkcorp/locale/en-US';
import { esLocale } from '@solinkcorp/locale/es';
import { frLocale } from '@solinkcorp/locale/fr';
import { DEFAULT_LOCALE, getCountryNameFromAddress } from '@solinkcorp/vd-common';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';
import merge from 'lodash/merge';
import moment from 'moment-timezone';
import React from 'react';
import type { PartialDeep } from 'type-fest';
import { TIME_FORMAT } from 'vd-constants';

interface LocaleEntry {
  mapping: LocaleDefinition;
  defaultFor?: string;
}

const LANGUAGE_NAMES: LocaleDefinition['languages'] = [];

function isLocale(locale: string): locale is LocaleDefinition['locale'] {
  return LANGUAGES.includes(locale as any);
}

/**
 * Takes a locale from a JSON file and returns a locale object with localeCode and languages.
 */
function makeMapping(localeCode: LocaleName, localeObject: LocaleJsonFile): LocaleDefinition {
  LANGUAGE_NAMES.push({
    value: localeCode,
    display: localeObject.name,
  });

  if (!isLocale(localeObject.locale)) {
    throw new Error(`Invalid locale: ${localeObject.locale}`);
  }

  return Object.freeze({
    ...localeObject,
    locale: localeObject.locale,
    localeCode,
    languages: LANGUAGE_NAMES,
  });
}

/**
 * Like `makeMapping`, but if any keys are missing from `localeObject`, they
 * will automatically be filled in from `parentLocaleObject`.  This makes it
 * so if you define a key in en-CA, for example, you don't need to also define
 * it in en-US and en-GB, unless it is different.
 */
function makeMappingWithDefaults(
  localeCode: LocaleName,
  parentLocaleObject: LocaleJsonFile,
  localeObject: PartialDeep<LocaleJsonFile>,
): LocaleDefinition {
  const finalLocaleObject = merge({}, parentLocaleObject, localeObject);
  return makeMapping(localeCode, finalLocaleObject);
}

const SUPPORTED_LOCALES: {
  [LOCALE in LocaleName]: LocaleEntry;
} = {
  'en-CA': { mapping: makeMapping('en-CA', enCALocale), defaultFor: 'en' },
  'en-US': { mapping: makeMappingWithDefaults('en-US', enCALocale, enUSLocale) },
  'en-GB': { mapping: makeMappingWithDefaults('en-GB', enCALocale, enGBLocale) },
  'fr-FR': { mapping: makeMapping('fr-FR', frLocale), defaultFor: 'fr' },
  'es-ES': { mapping: makeMapping('es-ES', esLocale), defaultFor: 'es' },
};

export function isValidLocale(locale: string): locale is LocaleName {
  return !!SUPPORTED_LOCALES[locale as LocaleName];
}

/**
 * Generate a mapping with the short code. E.g. {'en': 'en-CA'}
 *
 * @returns {Object}
 */
function generateShortMapping() {
  const mapping: { [shortCode: string]: LocaleName } = {};
  for (const [localeCode, localeObject] of Object.entries(SUPPORTED_LOCALES)) {
    const shortCode = localeObject.defaultFor;
    if (shortCode) {
      mapping[shortCode] = localeCode as LocaleName;
    }
  }
  return mapping;
}

/**
 * Generate a supported locale code.
 *
 * @param {string} localeCode - The locale code to check whether it matches or generate a supported locale code from
 * @param {Object.<string, string>} supportedLanguageCodes - Supported language code to locale code mappings
 * @returns {string|undefined}
 */
function generateLocaleCode(
  localeCode: string,
  supportedLanguageCodes: { [key: string]: string } = {},
) {
  let supportedCode;

  const [languageCode, regionCode] = localeCode.split('-');
  const formattedLocaleCode = regionCode
    ? `${languageCode}-${regionCode.toUpperCase()}`
    : `${languageCode}`;

  if (has(SUPPORTED_LOCALES, formattedLocaleCode)) {
    supportedCode = formattedLocaleCode;
  } else {
    // It will either assign the default locale for the language (e.g. 'en-CA' for 'en') or it will be undefined
    supportedCode = supportedLanguageCodes[languageCode];
  }

  return supportedCode;
}

/**
 * Try to determine a supported locale code.
 *
 * NOTE: Intended to be used only when the user has no locale stored in his/her preferences.
 *
 * @returns {LocaleName}
 */
export function determineLocaleCode(localeCode?: LocaleName): LocaleName {
  let supportedLocaleCode;
  const supportedLanguageCodes = generateShortMapping();

  if (localeCode) {
    supportedLocaleCode = generateLocaleCode(localeCode, supportedLanguageCodes);
  } else if (window.navigator.languages && window.navigator.languages.length > 0) {
    for (const code of window.navigator.languages) {
      supportedLocaleCode = generateLocaleCode(code, supportedLanguageCodes);
      if (supportedLocaleCode) {
        break;
      }
    }
  } else if (window.navigator.language) {
    supportedLocaleCode = generateLocaleCode(window.navigator.language, supportedLanguageCodes);
  }

  return (supportedLocaleCode || DEFAULT_LOCALE) as LocaleName;
}

/**
 * Return the locale definition for a locale code.
 */
export function getLocale(localeCode: LocaleName): LocaleDefinition {
  return SUPPORTED_LOCALES[localeCode].mapping;
}

/**
 * Return the locale definition name sans locale.
 */
export function getLanguage(localeCode: LocaleName): string {
  return getLocale(localeCode).name.split(' (')[0]; // Remove the locale in parentheses from the name
}

/**
 * Check if the provided locale code is supported in the system.
 *
 * @param {string} localeCode
 * @returns {boolean}
 */
export function isSupportedLocale(localeCode: string): localeCode is LocaleName {
  return Object.keys(SUPPORTED_LOCALES).indexOf(localeCode) !== -1;
}

/**
 * Get the language code, normalized to 'en-CA' for English.
 *
 * @param {string} localeCode
 * @returns {string} normalized language code
 */
export function getNormalizedCode(localeCode: LocaleName): string {
  return localeCode.includes('en-') ? 'en-CA' : localeCode;
}

export function getValueByLocale({
  values,
  localeCode,
}: {
  values: { [L in LocaleName]: string | undefined };
  localeCode: LocaleName;
}): string | undefined {
  let output = get(values, localeCode);

  if (output === undefined) {
    // If the value doesn't exist in the current locale, try a similar locale
    const localeCodePrefix = localeCode.split('-')[0];
    const keys = Object.keys(values);
    const similarLocale = find(keys, (key) => key.includes(localeCodePrefix));
    if (similarLocale) {
      output = get(values, similarLocale);
    } else {
      // Otherwise, fallback to Canadian English, if available
      output = get(values, 'en-CA');
    }
  }

  return output;
}

export function isLocationUS(location: { address: string }) {
  if (location) {
    return getCountryNameFromAddress(location.address) === 'USA';
  }
  return false;
}

const placeholderRegex = new RegExp('__[a-zA-Z0-9]+__');

/**
 * Replace placeholders in a translated string.
 */
export function getLocalizedStr({
  string,
  replacements,
}: {
  string: string;
  replacements?: { [key: string]: any };
}) {
  let formattedLocaleString = string;

  // Placeholder text takes on the form of "__placeholderName__" in the localization string
  if (replacements) {
    for (const [placeHolderName, replacement] of Object.entries(replacements)) {
      formattedLocaleString = formattedLocaleString.replace(
        `__${placeHolderName}__`,
        `${replacement}`,
      );
    }
  }

  // Check if all placeholder strings have been replaced
  if (placeholderRegex.test(formattedLocaleString)) {
    throw new Error(`Missing replacement string(s) in "${formattedLocaleString}"`);
  }

  return formattedLocaleString;
}

/**
 * Essentially the same as getLocalizedStr, except the replacements can be JSX
 * This way you can style the substrings
 * It will return an array of strings and JSX elements
 * For e.g getLocalizedJSX('This text is __styled__', { styled: <span style={{ color: 'red'}}>red<span/>})
 * Will return ['This text is ', <span style={{ color: 'red'}}>red<span/>]
 */
export function getLocalizedJSX(
  message: string,
  replacements?: { [key: string]: any },
): React.ReactNode {
  const localizedJSX = message.split('__');
  if (replacements) {
    for (const [placeHolderName, replacement] of Object.entries(replacements)) {
      localizedJSX[localizedJSX.indexOf(placeHolderName)] = replacement;
    }
  }
  return localizedJSX;
}

/**
 * Quite similar to getLocalizedJSX, but this one is used for replacing tags with a wrapper
 * @param message the localized message containing {tag}text{/tag}
 * @param tag the tag string e.g. 'strong', 'bold', etc.
 * @param wrapper a function that takes the text and returns a React node
 * @returns your wrapped translation text
 * @example getTagReplacedLocalizedJSX('This is {emphasis}bold{/emphasis} text', 'emphasis', (text) => <strong>{text}</strong>)
 * => ['This is ', <strong>bold</strong>, ' text]
 */
export const getTagReplacedLocalizedJSX = (
  message: string,
  tag: string,
  wrapper: (content: string) => React.ReactNode,
): React.ReactNode[] => {
  const result: React.ReactNode[] = [];

  const parts = message.split(new RegExp(`\\{${tag}\\}|\\{\\/${tag}\\}`, 'g'));

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i % 2 === 1) {
      const node = wrapper(part);
      const key = `${tag}-${i}`;
      result.push(
        React.isValidElement(node)
          ? React.cloneElement(node, { key })
          : React.createElement(React.Fragment, { key }, node),
      );
    } else {
      result.push(part);
    }
  }

  return result;
};

export const getShortLocaleCode = ({
  locale,
  localeCode,
}: {
  locale: LocaleDefinition;
  localeCode: LocaleName;
}) => get(locale, 'locale', localeCode.split('-')[0]);

// Putting this in its own function in case we ever need special handling for certain languages
export const getUpperCase = (localeString: string) => localeString.toUpperCase();

export const truncateWithEllipsis = (str: string, maxCharLength: number) => {
  if (str.length < maxCharLength) return str;
  return str.slice(0, maxCharLength) + '...';
};

export function getDefaultTimeFormat() {
  const localeData = moment.localeData();
  const longDataTimeFormat = get(localeData, '_longDateFormat.LT', '');
  if (longDataTimeFormat.includes('H') && !longDataTimeFormat.includes('A')) {
    return TIME_FORMAT.TWENTY_FOUR_HOUR;
  }
  return TIME_FORMAT.TWELVE_HOUR;
}

export function useTimeFormat() {
  return useUserPreferences()[0].timeFormat || getDefaultTimeFormat();
}

export default {
  determineLocaleCode,
  isSupportedLocale,
  getNormalizedCode,
  getValueByLocale,
  isLocationUS,
  getLocalizedStr,
  getLocalizedJSX,
  getShortLocaleCode,
  getUpperCase,
  getLocale,
};
